import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, fmtINR, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tray, CheckCircle, XCircle, ArrowRight, MagnifyingGlass, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import WhatsAppNudge from "@/components/WhatsAppNudge";

const KIND_ICON = { expense: "🧾", asset: "📦" };

export default function MyQueue() {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], count: 0 });
  const [busy, setBusy] = useState(null);
  const [centres, setCentres] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState(() => {
    try { return sessionStorage.getItem("myqueue-search") || ""; }
    catch (e) { console.debug("sessionStorage read failed", e); return ""; }
  });
  const [companyFilter, _setCompanyFilter] = useState(() => {
    try { return sessionStorage.getItem("myqueue-company-filter") || "all"; }
    catch (e) { console.debug("sessionStorage read failed", e); return "all"; }
  });
  const setCompanyFilter = (v) => {
    _setCompanyFilter(v);
    try { sessionStorage.setItem("myqueue-company-filter", v); }
    catch (e) { console.debug("sessionStorage write failed", e); }
  };
  useEffect(() => {
    try { sessionStorage.setItem("myqueue-search", search); }
    catch (e) { console.debug("sessionStorage write failed", e); }
  }, [search]);
  // Persist the centre filter across in-app navigation (open detail → approve
  // → back to My Queue) so the user lands back on the centre they filtered.
  const [centreFilter, _setCentreFilter] = useState(() => {
    try { return sessionStorage.getItem("myqueue-centre-filter") || "all"; }
    catch (e) { console.debug("sessionStorage read failed", e); return "all"; }
  });
  const setCentreFilter = (v) => {
    _setCentreFilter(v);
    try { sessionStorage.setItem("myqueue-centre-filter", v); }
    catch (e) { console.debug("sessionStorage write failed", e); }
  };
  // Quick actions go through a confirmation dialog so nothing is approved or
  // rejected by an accidental tap. Reject requires remarks (same rule as the
  // detail page). NOTE: declared before the centre_manager early-return below
  // so hooks order stays stable.
  const [confirm, setConfirm] = useState(null); // { item, action: "approve" | "reject" }
  const [confirmRemarks, setConfirmRemarks] = useState("");

  const load = () =>
    api.get("/inbox").then((r) => setData(r.data));

  useEffect(() => {
    if (user && user.role === "centre_manager") return;
    load();
    api.get("/centres").then((r) => setCentres(r.data));
    api.get("/companies")
      .then((r) => setCompanies(r.data))
      .catch((e) => console.warn("MyQueue: failed to load companies", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Hooks below MUST run before any conditional return to satisfy React's rules-of-hooks.
  const centreCompanyMap = useMemo(() => {
    const m = {};
    centres.forEach((c) => { m[c.id] = c.company_id || null; });
    return m;
  }, [centres]);

  const accessibleCentres = useMemo(
    () => centres.filter((c) => user?.all_centres || (user?.centre_ids || []).includes(c.id)),
    [centres, user],
  );

  if (user && user.role === "centre_manager") {
    return <Navigate to="/dashboard" replace />;
  }

  const q = search.trim().toLowerCase();
  const filteredItems = data.items.filter((i) => {
    if (centreFilter !== "all" && i.centre_id !== centreFilter) return false;
    if (companyFilter !== "all" && (centreCompanyMap[i.centre_id] || "__none__") !== companyFilter) return false;
    if (!q) return true;
    const hay = [i.number, i.title, i.vendor, i.centre_code, i.submitted_by, i.next_approver_role]
      .filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  });

  // Group the visible queue company-wise (Unassigned last) — simple sectioned view.
  const grouped = [];
  const gmap = {};
  filteredItems.forEach((i) => {
    const cid = centreCompanyMap[i.centre_id];
    const comp = cid ? companies.find((co) => co.id === cid) : null;
    const key = comp ? comp.id : "__none__";
    if (!gmap[key]) {
      gmap[key] = {
        key,
        slug: comp ? comp.code : "unassigned",
        name: comp ? comp.name : "Unassigned",
        items: [],
        total: 0,
      };
      grouped.push(gmap[key]);
    }
    gmap[key].items.push(i);
    gmap[key].total += Number(i.amount || 0);
  });
  grouped.sort((a, b) =>
    a.key === "__none__" ? 1 : b.key === "__none__" ? -1 : a.name.localeCompare(b.name));

  const openConfirm = (item, action) => {
    setConfirm({ item, action });
    setConfirmRemarks("");
  };

  const doConfirm = async () => {
    if (!confirm) return;
    const { item, action } = confirm;
    if (action === "reject" && !confirmRemarks.trim()) {
      return toast.error("Remarks are required to reject.");
    }
    setBusy(item.id);
    try {
      const base = item.kind === "expense" ? "/expenses" : "/assets";
      await api.post(`${base}/${item.id}/${action}`, {
        remarks: action === "reject" ? confirmRemarks.trim() : "",
      });
      toast.success(`${action === "approve" ? "Approved" : "Rejected"} ${item.number}`);
      setConfirm(null);
      // Remove the acted item instantly so the view moves to the next pending
      // record; then refresh from the server for authoritative state.
      setData((d) => ({
        count: Math.max(0, d.count - 1),
        items: d.items.filter((x) => x.id !== item.id),
      }));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  // Spec §20: show centre filter whenever the user has access to ≥2 centres,
  // even if the queue is currently empty for some of them.
  const centreOptions = accessibleCentres.length > 1 ? accessibleCentres : [];

  return (
    <div className="p-4 lg:p-8 space-y-6" data-testid="myqueue-root">
      <div className="flex justify-between items-start sm:items-end gap-3">
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Inbox</div>
          <h1 className="font-heading text-3xl sm:text-4xl mt-1 tracking-tight">My Queue</h1>
          <p className="text-xs text-zinc-500 mt-2">
            Every expense &amp; asset sitting at your approval level. Review each record individually before approving.
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Pending</div>
          <div className="font-heading text-2xl sm:text-3xl tabular-nums" data-testid="myqueue-count">{data.count}</div>
        </div>
      </div>

      {data.count > 0 && (
        <div className="flex flex-wrap items-center gap-3 bg-white border border-zinc-200 p-3" data-testid="myqueue-filters">
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search number, title, vendor, centre, submitter…"
              data-testid="myqueue-search"
              className="rounded-sm h-9 pl-8 pr-8"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                data-testid="myqueue-search-clear"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
              >
                <X size={14} />
              </button>
            )}
          </div>
          {companies.length >= 1 && (
            <div className="flex items-center gap-2" data-testid="myqueue-company-filter-wrap">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Company</span>
              <select
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value)}
                data-testid="myqueue-company-filter"
                className="rounded-sm border border-zinc-300 h-9 px-3 text-sm bg-white"
              >
                <option value="all">All companies</option>
                {companies.map((co) => {
                  const count = data.items.filter((i) => centreCompanyMap[i.centre_id] === co.id).length;
                  return <option key={co.id} value={co.id}>{co.code} · {co.name} ({count})</option>;
                })}
                <option value="__none__">Unassigned ({data.items.filter((i) => !centreCompanyMap[i.centre_id]).length})</option>
              </select>
            </div>
          )}
          {centreOptions.length > 1 && (
            <div className="flex items-center gap-2" data-testid="myqueue-centre-filter-wrap">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Centre</span>
              <select
                value={centreFilter}
                onChange={(e) => setCentreFilter(e.target.value)}
                data-testid="myqueue-centre-filter"
                className="rounded-sm border border-zinc-300 h-9 px-3 text-sm bg-white"
              >
                <option value="all">All my centres ({data.items.length})</option>
                {centreOptions.map((c) => {
                  const count = data.items.filter((i) => i.centre_id === c.id).length;
                  return <option key={c.id} value={c.id}>{c.code} · {c.name} ({count})</option>;
                })}
              </select>
            </div>
          )}
          {(centreFilter !== "all" || companyFilter !== "all" || search) && (
            <button
              onClick={() => { setCentreFilter("all"); setCompanyFilter("all"); setSearch(""); }}
              className="text-xs text-zinc-600 hover:text-black underline"
              data-testid="myqueue-clear-filters"
            >
              Clear all
            </button>
          )}
        </div>
      )}

      {data.count === 0 ? (
        <div className="bg-white border border-zinc-200 p-16 text-center">
          <Tray size={40} className="mx-auto text-zinc-300 mb-4" />
          <p className="text-sm text-zinc-500">Nothing awaiting your action — you&apos;re all caught up.</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-16 text-center">
          <Tray size={40} className="mx-auto text-zinc-300 mb-4" />
          <p className="text-sm text-zinc-500">No records match your filters. Try clearing them to see everything.</p>
        </div>
      ) : (
        <div className="space-y-6" data-testid="myqueue-groups">
          {grouped.map((g) => (
            <div key={g.key} className="bg-white border border-zinc-200"
              data-testid={`queue-company-group-${g.slug}`}>
              <div className="flex items-center justify-between px-4 py-2.5 bg-zinc-50 border-b border-zinc-200 flex-wrap gap-2">
                <div className="flex items-baseline gap-2 min-w-0">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 shrink-0">Company</span>
                  <span className="font-heading text-base truncate" data-testid={`queue-company-name-${g.slug}`}>{g.name}</span>
                </div>
                <div className="text-xs text-zinc-500 tabular-nums" data-testid={`queue-company-meta-${g.slug}`}>
                  {g.items.length} pending · {fmtINR(g.total)}
                </div>
              </div>
              <div className="divide-y divide-zinc-100">
          {g.items.map((item) => (
            <div
              key={`${item.kind}:${item.id}`}
              className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 hover:bg-zinc-50"
              data-testid={`queue-item-${item.number}`}
            >
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="text-2xl shrink-0">{KIND_ICON[item.kind]}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-zinc-500">{item.number}</span>
                    <span className="text-xs uppercase tracking-widest text-zinc-400">{item.kind}</span>
                    <span className="px-2 py-0.5 text-[10px] uppercase tracking-widest bg-amber-50 text-amber-800 border border-amber-300">
                      {item.next_approver_role}
                    </span>
                  </div>
                  <div className="mt-1 flex items-baseline gap-3 flex-wrap">
                    <div className="text-sm font-medium truncate">{item.title || "—"}</div>
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5 truncate">
                    {item.centre_code} · {item.vendor || "—"} · by {item.submitted_by}
                  </div>
                </div>
              </div>
              <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-center gap-2 sm:gap-3 sm:shrink-0 sm:min-w-[120px]">
                <div className="sm:text-right">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Amount</div>
                  <div className="font-heading text-lg tabular-nums">{fmtINR(item.amount)}</div>
                </div>
                <div className="flex items-center gap-2 sm:flex-col sm:items-end">
                  <WhatsAppNudge kind={item.kind} id={item.id} number={item.number} variant="icon" />
                  <Button
                    size="sm"
                    onClick={() => openConfirm(item, "approve")}
                    disabled={busy === item.id}
                    data-testid={`queue-approve-${item.number}`}
                    className="rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white text-xs gap-1"
                  >
                    <CheckCircle size={12} weight="bold" /> {busy === item.id ? "…" : "Approve"}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => openConfirm(item, "reject")}
                    disabled={busy === item.id}
                    data-testid={`queue-reject-${item.number}`}
                    className="rounded-sm bg-rose-700 hover:bg-rose-800 text-white text-xs gap-1"
                  >
                    <XCircle size={12} weight="bold" /> Reject
                  </Button>
                  <Link
                    to={item.link}
                    state={{ from: "/my-queue" }}
                    className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-black flex items-center gap-1"
                    data-testid={`queue-open-${item.number}`}
                  >
                    Open <ArrowRight size={10} />
                  </Link>
                </div>
              </div>
            </div>
          ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirmation dialog for quick approve / reject */}
      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent className="bg-white rounded-sm max-w-md w-[calc(100vw-2rem)]" data-testid="queue-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {confirm?.action === "approve" ? "Confirm Approval" : "Confirm Rejection"}
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              {confirm?.action === "approve"
                ? `Approve ${confirm?.item?.kind} ${confirm?.item?.number} (${fmtINR(confirm?.item?.amount || 0)})? It will move to the next approval step.`
                : `Reject ${confirm?.item?.kind} ${confirm?.item?.number} (${fmtINR(confirm?.item?.amount || 0)})? The submitter will be notified.`}
            </DialogDescription>
          </DialogHeader>
          {confirm?.action === "reject" && (
            <Textarea
              placeholder="Remarks (required) — why is this being rejected?"
              value={confirmRemarks}
              onChange={(e) => setConfirmRemarks(e.target.value)}
              className="rounded-sm border-zinc-300 text-sm"
              rows={3}
              data-testid="queue-confirm-remarks"
            />
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirm(null)}
                    className="rounded-sm" data-testid="queue-confirm-cancel">
              Cancel
            </Button>
            <Button
              onClick={doConfirm}
              disabled={busy === confirm?.item?.id}
              data-testid="queue-confirm-submit"
              className={`rounded-sm text-white ${
                confirm?.action === "approve"
                  ? "bg-emerald-700 hover:bg-emerald-800"
                  : "bg-rose-700 hover:bg-rose-800"
              }`}
            >
              {busy === confirm?.item?.id
                ? "Working…"
                : confirm?.action === "approve" ? "Yes, Approve" : "Yes, Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
